import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TableCompareTrigger } from "../../../components/lcaComparison";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addInputsFromRepair41_3aLCA,
  deleteInputsFromRepair41_3aLCA,
  getAllInputsFromRepair41_3aLCA,
  updateInputsFromRepair41_3aLCA,
} from "../../../../../../apis/Product/UseStageFlow41Api";
import { REFRESH_USE_STAGE_41_3_TOTALS } from "../../../ProdRepair41_3/Header";
import {
  getProductMaterialUsage34_1aDropdown,
  getProductEnergyInputMaintenance41_2aDropdown,
  getProductWaterConsumptionMaintenance41_2aDropdown,
} from "../../../../../../apis/Dropdown";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const InputsFromRepair41_3aTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [materialDropdown, setMaterialDropdown] = useState([]);
  const [energyDropdown, setEnergyDropdown] = useState([]);
  const [waterDropdown, setWaterDropdown] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [unitEnergy, setUnitEnergy] = useState({});
  const [unitWater, setUnitWater] = useState({});

  // Fetch dropdowns - using same dropdowns as 41.2a
  useEffect(() => {
    getProductMaterialUsage34_1aDropdown({
      setDropdown: setMaterialDropdown,
    });
    getProductEnergyInputMaintenance41_2aDropdown({
      setDropdown: setEnergyDropdown,
    });
    getProductWaterConsumptionMaintenance41_2aDropdown({
      setDropdown: setWaterDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllInputsFromRepair41_3aLCA({
      productId,
      setObjects: (rows) => {
        setData(rows);
        window.dispatchEvent(new Event(REFRESH_USE_STAGE_41_3_TOTALS));
      },
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units when dropdowns or data change
  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      const newUnitEnergy = {};
      const newUnitWater = {};
      const newFiles = {};

      data.forEach((item) => {
        if (item.material_supporting_repair) {
          const material = materialDropdown.find(
            (m) =>
              m.code === item.material_supporting_repair ||
              m.value === item.material_supporting_repair ||
              m.id === item.material_supporting_repair
          );
          if (material) {
            newUnitMaterials[item.id] = material.unit || "unit";
          }
        }

        if (item.energy_input_during_repair) {
          const energy = energyDropdown.find(
            (e) =>
              e.code === item.energy_input_during_repair ||
              e.value === item.energy_input_during_repair ||
              e.id === item.energy_input_during_repair
          );
          if (energy) {
            newUnitEnergy[item.id] = energy.unit || "unit";
          }
        }

        if (item.water_consumption_during_repair) {
          const water = waterDropdown.find(
            (w) =>
              w.code === item.water_consumption_during_repair ||
              w.value === item.water_consumption_during_repair ||
              w.id === item.water_consumption_during_repair
          );
          if (water) {
            newUnitWater[item.id] = water.unit || "unit";
          }
        }

      });

      setUnitMaterials(newUnitMaterials);
      setUnitEnergy(newUnitEnergy);
      setUnitWater(newUnitWater);
    }
  }, [data, materialDropdown, energyDropdown, waterDropdown]);


  const handleEdit = useCallback(
    (editedItem) => {
      const materialCode =
        typeof editedItem.material_supporting_repair === "string"
          ? editedItem.material_supporting_repair
          : editedItem.material_supporting_repair?.value ||
            editedItem.material_supporting_repair?.code ||
            null;

      const energyCode =
        typeof editedItem.energy_input_during_repair === "string"
          ? editedItem.energy_input_during_repair
          : editedItem.energy_input_during_repair?.value ||
            editedItem.energy_input_during_repair?.code ||
            null;

      const waterCode =
        typeof editedItem.water_consumption_during_repair === "string"
          ? editedItem.water_consumption_during_repair
          : editedItem.water_consumption_during_repair?.value ||
            editedItem.water_consumption_during_repair?.code ||
            null;

      updateInputsFromRepair41_3aLCA({
        productId,
        rowId: editedItem.id,
        name_repair_process: editedItem.name_repair_process || null,
        describe_repair_process: editedItem.describe_repair_process || null,
        material_supporting_repair: materialCode,
        quantity_per_fu_material: editedItem.quantity_per_fu_material || 0,
        energy_input_during_repair: energyCode,
        quantity_per_fu_energy: editedItem.quantity_per_fu_energy || 0,
        water_consumption_during_repair: waterCode,
        quantity_per_fu_water: editedItem.quantity_per_fu_water || 0,
        repair_processes_per_fu_lifetime: editedItem.repair_processes_per_fu_lifetime || 0,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () =>
          window.dispatchEvent(new Event(REFRESH_USE_STAGE_41_3_TOTALS)),
      });
    },
    [productId, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const materialCode =
        typeof newItem.material_supporting_repair === "string"
          ? newItem.material_supporting_repair
          : newItem.material_supporting_repair?.value ||
            newItem.material_supporting_repair?.code ||
            null;

      const energyCode =
        typeof newItem.energy_input_during_repair === "string"
          ? newItem.energy_input_during_repair
          : newItem.energy_input_during_repair?.value ||
            newItem.energy_input_during_repair?.code ||
            null;

      const waterCode =
        typeof newItem.water_consumption_during_repair === "string"
          ? newItem.water_consumption_during_repair
          : newItem.water_consumption_during_repair?.value ||
            newItem.water_consumption_during_repair?.code ||
            null;

      addInputsFromRepair41_3aLCA({
        productId,
        name_repair_process: newItem.name_repair_process || null,
        describe_repair_process: newItem.describe_repair_process || null,
        material_supporting_repair: materialCode,
        quantity_per_fu_material: newItem.quantity_per_fu_material || 0,
        energy_input_during_repair: energyCode,
        quantity_per_fu_energy: newItem.quantity_per_fu_energy || 0,
        water_consumption_during_repair: waterCode,
        quantity_per_fu_water: newItem.quantity_per_fu_water || 0,
        repair_processes_per_fu_lifetime: newItem.repair_processes_per_fu_lifetime || 0,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteInputsFromRepair41_3aLCA({
        productId,
        rowId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);


  const columns = useMemo(
    () => [
      {
        id: "name_repair_process",
        accessor: "name_repair_process",
        header: `${t("NAME_REPAIR_PROCESS") || "Name repair process"} *`,
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: "describe_repair_process",
        accessor: "describe_repair_process",
        header: `${t("DESCRIBE_REPAIR_PROCESS") || "Describe repair process"} *`,
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: "material_supporting_repair",
        accessor: "material_supporting_repair",
        header: `${t("MATERIAL_SUPPORTING_REPAIR") || "Material supporting the repair process"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_MATERIAL") || "Select material...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "material_supporting_repair",
            materialDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "quantity_per_fu_material",
        accessor: "quantity_per_fu_material",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "material_supporting_repair",
            materialDropdown,
            unitMaterials,
            rowId
          ),
      },
      {
        id: "energy_input_during_repair",
        accessor: "energy_input_during_repair",
        header: `${t("ENERGY_INPUT_DURING_REPAIR") || "Energy input during repair"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_ENERGY_TYPE") || "Select energy type...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "energy_input_during_repair",
            energyDropdown,
            unitEnergy,
            rowId
          ),
      },
      {
        id: "quantity_per_fu_energy",
        accessor: "quantity_per_fu_energy",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "energy_input_during_repair",
            energyDropdown,
            unitEnergy,
            rowId
          ),
      },
      {
        id: "water_consumption_during_repair",
        accessor: "water_consumption_during_repair",
        header: `${t("WATER_CONSUMPTION_DURING_REPAIR") || "Water consumption during repair"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_WATER_TYPE") || "Select water type...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "water_consumption_during_repair",
            waterDropdown,
            unitWater,
            rowId
          ),
      },
      {
        id: "quantity_per_fu_water",
        accessor: "quantity_per_fu_water",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "water_consumption_during_repair",
            waterDropdown,
            unitWater,
            rowId
          ),
      },
      {
        id: "repair_processes_per_fu_lifetime",
        accessor: "repair_processes_per_fu_lifetime",
        header: `${t("REPAIR_PROCESSES_PER_FU_LIFETIME") || "Repair processes per functional unit during lifetime"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
          <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitMaterials,
      unitEnergy,
      unitWater,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      material_supporting_repair: materialDropdown || [],
      energy_input_during_repair: energyDropdown || [],
      water_consumption_during_repair: waterDropdown || [],
    }),
    [materialDropdown, energyDropdown, waterDropdown]
  );


  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({
          [impactCategoryAccessor]: CustomInputComponent,
        }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default InputsFromRepair41_3aTable;

